import { useEffect, useRef } from "react"
import { usePage } from "@inertiajs/react"
import { Toaster, toast } from "sonner"
import type { SharedProps } from "@/types/inertia"

// Bridges Rails flash → sonner toasts so flashes don't eat page real
// estate. Mounts the global <Toaster /> exactly once (here, inside
// AppShell) and watches Inertia shared `props.flash`. Each unique
// notice/alert fires once via toast.success / toast.error.
//
// Console mirroring (info/warn) is preserved — DevTools still sees the
// flash even when the operator never reads the toast (scrolled, dismissed,
// or running an integration test headless).
//
// Why a "last seen" ref instead of dismiss-state: sonner manages its
// own queue + dismissal. We only need to avoid double-firing on
// re-renders that didn't change the message.
export function FlashBanner() {
  const { props } = usePage<SharedProps>()
  const flash  = props.flash ?? {}
  const notice = flash.notice ?? null
  const alert  = flash.alert ?? null

  const lastNoticeRef = useRef<string | null>(null)
  const lastAlertRef  = useRef<string | null>(null)

  useEffect(() => {
    if (notice && lastNoticeRef.current !== notice) {
      lastNoticeRef.current = notice
      // eslint-disable-next-line no-console
      console.info("[flash:notice]", notice)
      toast.success(notice)
    }
  }, [notice])

  useEffect(() => {
    if (alert && lastAlertRef.current !== alert) {
      lastAlertRef.current = alert
      // eslint-disable-next-line no-console
      console.warn("[flash:alert]", alert)
      toast.error(alert)
    }
  }, [alert])

  return (
    <Toaster
      position="bottom-right"
      theme="dark"
      richColors
      closeButton
      duration={4000}
    />
  )
}
